/**
 * ScenarioInputGroup — the shared scenario-driver rows renderer (spec §2,
 * "one renderer, two surfaces"): the per-driver row (label, numeric cell,
 * percent ×100 view with divide-back on commit, onEndEditing commit)
 * extracted from TargetPriceDraftSheet so the sheet and the wizard bubble
 * render the SAME rows configured differently — bubble-sheet disagreement on
 * values, formatting, or commit semantics is impossible on the client.
 *
 * ROWS ONLY: accept chips, the 「全部確認」 row, and the collapsible
 * explainer panels compose AROUND this component (Task 11) — none of them
 * live here. Labels arrive via props (the callers own i18n); the per-row
 * `rationale` is the bubble's carrier for the card's per-scenario reasoning
 * (the sheet keeps its header-level rationale prose and passes none).
 *
 * - percent rows render the ×100 view of the card's decimal (pctToDraft) and
 *   divide back on commit: onCommit receives CARD-UNIT strings ("20" →
 *   "0.2") — the caller's applyEdit path never divides again.
 * - junk drafts never reach onCommit (keep the last good value); clearing
 *   commits an empty raw — the caller turns '' into a null value.
 * - editable=false (bubble displacement: an earlier stage's group) renders
 *   the confirmed values as read-only text — no TextInputs — and cannot
 *   commit even if a handler was passed.
 * - a caller's OWN confirm affordance sweeps uncommitted drafts through the
 *   ref's {@link ScenarioInputGroupHandle.flushDirty} — rows commit on
 *   onEndEditing, so a confirm that does not flush silently drops a value the
 *   user typed but never blurred.
 * - Drafts are local and persist for the component's lifetime; a fresh
 *   surface state (sheet reopen) remounts via the caller's key.
 *
 * ROW STYLES ARE SHARED BY IDENTITY, not re-derived per consumer: the sheet's
 * createThemedStyles wires its label/driverRow/driverMain/input/confirmSpacer
 * keys to getTpRowStyles(theme, 'sheet'), the same cached singletons this
 * renderer applies. One theme, one set of style objects — the sheet and the
 * bubble cannot drift on row geometry or colors, and stable identities keep
 * the render tree free of per-render style allocations (the sheet suite
 * treats style-object identity as observable). The cache key includes the
 * theme id, so a theme switch rebuilds exactly once.
 */
import React, { useImperativeHandle, useRef, useState } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';

export type ScenarioInputGroupMode = 'sheet' | 'bubble';

/** One driver row. `value` is the CARD's number (decimal, never the percent
 * view); `percent` renders ×100 and divides back on commit. */
export type ScenarioInputRow = {
  key: string;
  label: string;
  value: number | null;
  rationale?: string | null;
  percent?: boolean;
};

/** Imperative draft sweep for the caller's OWN confirm affordance. Rows commit
 * on onEndEditing, so a value typed and then confirmed without tapping away
 * would be dropped while the input still displays it (2026-10-28 staging: the
 * bear EBIT margin never reached the card). `flushDirty` commits every dirty
 * row through the ordinary `onCommit` path AND returns what it committed in
 * CARD units ('' = cleared), because the caller cannot read this tick's commit
 * back out of React state. */
export type ScenarioInputGroupHandle = {
  flushDirty: () => { key: string; value: string }[];
};

/** Percent view of a card decimal: 0.1 → "10" (precision-safe ×100). */
export function pctToDraft(v: number | null | undefined): string {
  return v == null ? '' : String(Number((v * 100).toPrecision(12)));
}

/** Plain view of a card number. */
export function numToDraft(v: number | null | undefined): string {
  return v == null ? '' : String(v);
}

export const ScenarioInputGroup = React.forwardRef<
  ScenarioInputGroupHandle,
  {
    mode: ScenarioInputGroupMode;
    rows: ScenarioInputRow[];
    editable: boolean;
    /** One row's committed edit, raw in CARD units ('' = cleared). */
    onCommit?: (key: string, raw: string) => void;
    testIDPrefix: string;
  }
>(function ScenarioInputGroup({ mode, rows, editable, onCommit, testIDPrefix }, ref) {
  const { theme } = useTheme();
  const styles = getTpRowStyles(theme, mode);

  // Local drafts for the free-typed fields. Rendering falls back to the
  // canonical view of the row's value until the user types, so external card
  // updates always show through untouched rows; a typed draft sticks (the
  // last-writer-wins rule the sheet already follows).
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // The draft text each key has ALREADY committed. A draft survives its commit
  // (the bubble's row values are frozen server metadata, so dropping it would
  // snap the input back to the suggestion), which means "differs from the
  // displayed value" alone cannot tell a dirty row from a committed one.
  const committedRef = useRef<Record<string, string>>({});

  const viewOf = (row: ScenarioInputRow): string =>
    row.percent ? pctToDraft(row.value) : numToDraft(row.value);

  // What one row would commit, in CARD units — null when there is nothing to
  // commit. Shared by the blur path and flushDirty so the two cannot disagree
  // on what counts as a commit.
  const commitOf = (row: ScenarioInputRow): string | null => {
    const draft = drafts[row.key];
    if (draft === undefined) return null; // untouched
    if (draft === committedRef.current[row.key]) return null; // already committed
    const raw = draft.trim();
    // PR-review H2 no-op guard: typed back to the displayed value — commit
    // nothing. A no-op commit would POST the row (a transcript edit row +
    // provenance stamp for a change that never happened) and, on percent rows,
    // drift the value (JS String(9.7/100) = "0.09699…").
    if (raw === viewOf(row)) return null;
    if (!raw) return ''; // cleared
    const n = Number(raw);
    if (!Number.isFinite(n)) return null; // junk; keep the last good value
    return row.percent ? String(n / 100) : raw;
  };

  const commitRow = (row: ScenarioInputRow): string | null => {
    if (!onCommit || !editable) return null;
    const raw = commitOf(row);
    if (raw == null) return null;
    onCommit(row.key, raw);
    committedRef.current[row.key] = drafts[row.key];
    return raw;
  };

  useImperativeHandle(
    ref,
    () => ({
      flushDirty: () => {
        const flushed: { key: string; value: string }[] = [];
        rows.forEach((row) => {
          const raw = commitRow(row);
          if (raw != null) flushed.push({ key: row.key, value: raw });
        });
        return flushed;
      },
    }),
    [rows, commitRow]
  );

  return (
    <>
      {rows.map((row) => (
        <View key={row.key} style={styles.driverRow}>
          <View style={styles.driverMain}>
            <Text style={styles.label}>{row.label}</Text>
            {!!row.rationale && (
              <Text testID={`${testIDPrefix}-rationale-${row.key}`} style={styles.rowRationale}>
                {row.rationale}
              </Text>
            )}
          </View>
          {editable ? (
            <TextInput
              testID={`${testIDPrefix}-${row.key}`}
              style={styles.input}
              value={drafts[row.key] ?? viewOf(row)}
              onChangeText={(v) => setDrafts((d) => ({ ...d, [row.key]: v }))}
              onEndEditing={() => commitRow(row)}
              keyboardType={row.percent ? 'decimal-pad' : 'numbers-and-punctuation'}
              placeholder="0.00"
              placeholderTextColor={theme.colors.textTertiary}
            />
          ) : (
            // Displacement: the confirmed value, read-only — a group that
            // renders nothing editable cannot POST (spec §2).
            <Text testID={`${testIDPrefix}-${row.key}`} style={styles.readonlyValue}>
              {viewOf(row)}
            </Text>
          )}
          {/* Sheet rows carry the ✓-era spacer to keep row geometry stable;
              bubble rows pack without it. */}
          {mode === 'sheet' && <View style={styles.confirmSpacer} />}
        </View>
      ))}
    </>
  );
});

// Module-level style cache: one row-StyleSheet per (theme, mode), shared by
// every instance AND by the sheet's own stylesheet (see createThemedStyles in
// TargetPriceDraftSheet) — stable singletons, never re-created per render.
type RowStyles = ReturnType<typeof createTpRowStyles>;
const rowStylesCache = new Map<string, RowStyles>();

export function getTpRowStyles(theme: any, mode: ScenarioInputGroupMode): RowStyles {
  const key = `${theme.id}:${mode}`;
  let styles = rowStylesCache.get(key);
  if (!styles) {
    styles = createTpRowStyles(theme, mode);
    rowStylesCache.set(key, styles);
  }
  return styles;
}

function createTpRowStyles(theme: any, mode: ScenarioInputGroupMode) {
  if (mode === 'bubble') {
    // Bubble rows: self-contained cells on the chat surface (elevated cell
    // background, de-emphasized label/rationale), no ✓-era spacer.
    return StyleSheet.create({
      driverRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
        marginTop: 0,
      },
      driverMain: {
        flex: 2,
        minWidth: 0,
        gap: theme.spacing.xxs,
      },
      label: {
        fontSize: theme.typography.fontSizeSm,
        fontWeight: theme.typography.fontWeightSemibold,
        color: theme.colors.textSecondary,
      },
      rowRationale: {
        fontSize: theme.typography.fontSizeXs,
        lineHeight: theme.typography.fontSizeXs * 1.5,
        color: theme.colors.textSecondary,
      },
      input: {
        flex: 2,
        minWidth: 0,
        borderWidth: theme.id === 'bauhaus' ? 2 : 1,
        borderRadius: theme.radii.md,
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        fontSize: theme.typography.fontSizeMd,
        backgroundColor: theme.colors.backgroundElevated,
        borderColor: theme.colors.border,
        color: theme.colors.text,
      },
      readonlyValue: {
        flex: 2,
        fontSize: theme.typography.fontSizeMd,
        fontWeight: theme.typography.fontWeightSemibold,
        textAlignVertical: 'center',
        color: theme.colors.text,
      },
      confirmSpacer: { width: theme.spacing.xxl },
    });
  }
  // Sheet rows: the sheet's exact driver-row geometry (formerly inline in
  // TargetPriceDraftSheet) — same objects the sheet's stylesheet references.
  return StyleSheet.create({
    driverRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.xxs,
    },
    driverMain: {
      flex: 2,
      minWidth: 0,
      gap: theme.spacing.xxs,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    rowRationale: {
      fontSize: theme.typography.fontSizeXs,
      lineHeight: theme.typography.fontSizeXs * 1.5,
      color: theme.colors.textSecondary,
    },
    input: {
      flex: 2,
      minWidth: 0,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      fontSize: theme.typography.fontSizeMd,
    },
    readonlyValue: {
      flex: 2,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textAlignVertical: 'center',
      color: theme.colors.text,
    },
    confirmSpacer: {
      width: theme.spacing.xxl,
    },
  });
}
